// SellersDataGrid.tsx

import React, {useContext, useEffect, useState} from 'react';
import {useQuery, useMutation} from 'react-query';
import {apiClient, IGetAllParams, DataGridPro, MuiDataGrid, Mui, MuiIcons} from '@leap4/frontend';
import {sellersBaseUrl} from '../../../config/generals';
import {ISellerJson} from './seller.interface';
import {SellerContext} from "./sellerContext";
import {formatDateDDMMYYYY} from "../../utils/functions";
import {filterCreatedSellers, prepareSellersForOutput} from "./funcions";

// Types from MuiDataGrid namespace
type GridColDef = MuiDataGrid.GridColDef;
type GridRenderCellParams<V = any, R = any> = MuiDataGrid.GridRenderCellParams<V, R>;
const { Button } = Mui;
const { DifferenceOutlined: DifferenceOutlinedIcon, OpenInNewOutlined: OpenInNewOutlinedIcon } = MuiIcons;



const fetchAllSellers = async (
    page: number,
    pageSize: number
): Promise<{ data: ISellerJson[]; total: number }> => {
    const params: IGetAllParams = {
        fields: {
            _id: 1,
            created: 1,
            isChanged: 1,
            isValidate: 1,
        },
        pageSize: pageSize,
        page: page + 1, // Adjust if API uses 1-based indexing
        sort: {created: -1},
    };
    const response = await apiClient.get(sellersBaseUrl, {params});
    return {
        data: response.data.results as ISellerJson[],
        total: response.data.count,
    };
};

const SellersDataGrid: React.FC = () => {
    const {
        setChosenSeller,
    } = useContext(SellerContext);


    // Pagination state
    const [page, setPage] = useState<number>(0); // zero-based page index
    const [pageSize, setPageSize] = useState<number>(300); // default page size
    const [rowCount, setRowCount] = useState<number>(0); // total number of rows

    // State for all sellers
    const [sellers, setSellers] = useState<ISellerJson[]>([]);
    const {
        data: sellersData,
        isLoading: isLoadingSellers,
    } = useQuery(['allSellers', page, pageSize], () => fetchAllSellers(page, pageSize), {
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes
        refetchOnReconnect: false,
    });

    const getSellerDataById = async (sellerId: string): Promise<ISellerJson> => {
        const res = await apiClient.get(`${sellersBaseUrl}/${sellerId}`);
        return res.data as ISellerJson;
    };

// Use useMutation to create a mutation for fetching seller data
    const {mutate: fetchSellerById} = useMutation(getSellerDataById, {
        onSuccess: (data) => {
            // Update the chosen seller in your context or state
            setChosenSeller(data);
        },
    });

// Handle the replace seller action when the button is clicked
    const handleReplaceSeller = (sellerId: string) => {
        fetchSellerById(sellerId);
        window.scrollTo({
            top: 0,
            behavior: 'smooth', // Optional for smooth scrolling
        });
    };

    const openSellerFileByID = async (sellerId: string) => {
        let sellerData = await getSellerDataById(sellerId);
        sellerData= prepareSellersForOutput(filterCreatedSellers(sellerData));
        const url = window.URL.createObjectURL(new Blob([JSON.stringify(sellerData)], { type: 'application/json' }));
        window.open(url, '_blank');
    }
    useEffect(() => {
        if (sellersData) {
            setSellers(sellersData.data);
            setRowCount(sellersData.total);
        }
    }, [sellersData]);


    const columns: GridColDef[] = [
        {
            field: 'created',
            headerName: 'Date',
            width: 200,
            valueFormatter: (value) => {
                if (!value) return 'N/A';
                return formatDateDDMMYYYY(new Date(value));
            },
        },
        {
            field: 'isValidate',
            headerName: 'Is Valid',
            width: 150,
            type: 'boolean',
            renderCell: (params: GridRenderCellParams<boolean>) => (params.value ? 'Yes' : 'No'),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Show diff from current',
            width: 200,
            renderCell: (params: GridRenderCellParams<any, ISellerJson>) => (
                <Button
                    variant={"text"}
                    color={"success"}
                    onClick={() => handleReplaceSeller(params.row._id)}
                    startIcon={<DifferenceOutlinedIcon/>}
                />
            ),

        },
        {
            field:'open file',
            headerName:'Open File',
            width:200,
            type:'actions',
            renderCell: (params: GridRenderCellParams<any, ISellerJson>) => (
                <Button
                    variant={"text"}
                    color={"inherit"}
                    onClick={() => openSellerFileByID(params.row._id)}
                    startIcon={<OpenInNewOutlinedIcon/>}
                />
            )
        }
    ];

    return (
        <>
            {/* TODO: FIX FOR VERSION 19.2.0 OF FRONTEND LIB - Remove disableColumnPinning once lib is updated */}
            <DataGridPro
                disableColumnPinning={true}
                rows={sellers}
                columns={columns}
                loading={isLoadingSellers}
                autoHeight
                getRowId={(row) => row._id}
                pagination
                paginationMode="server"
                rowCount={rowCount}
                page={page}
                onPageChange={(newPage) => setPage(newPage)}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(0); // Reset to first page when page size changes
                }}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
            />

        </>

    );
};

export default SellersDataGrid;
